import React, {
  CSSProperties,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { useWindowSize } from '@att-bit/duc.hooks.use-window-size';
import { useMutationObserver } from '@att-bit/duc.hooks.use-mutation-observer';
import { ExpandIcon } from './expand-icon';

export enum AccordionItemVariant {
  SMALL = 'small',
  MEDIUM = 'medium',
}

export type AccordionItemProps = {
  /**  children (text/jsx) to be rendered in component  */
  children: React.ReactNode;
  className?: string;
  /** sets body content to max width */
  contentWidth?: boolean;
  /** determines if AccordionBlock is disabled */
  disabled?: boolean;
  /** set to control default expanded or collapsed */
  isOpen?: boolean;
  /** event that is fired when this AccordionBlock is toggled */
  onToggle?: (isOpen: boolean) => void;
  /** Needed for Cato add id to content div */
  testId?: string;
  /** Determine the theme set to Accordion from feed */
  theme?: string;
  /** determines what the type of padding should be on top and bottom of the title */
  variant?: AccordionItemVariant;
};

export type AccordionItemBodyProps = {
  children?: React.ReactNode;
  contentWidth?: boolean;
  isOpen?: boolean;
  uniqueId?: string;
};

export type AccordionItemTitleProps = {
  children?: React.ReactNode;
  className?: string;
  contentWidth?: boolean;
  disabled?: boolean;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  testId?: string;
  theme?: string;
  variant?: AccordionItemVariant;
  onKeyUp?: () => {};
  onMouseDown?: () => {};
  uniqueId?: string;
};

// for memo: export const AccordionItem = React.memo(({
export const AccordionItem = ({
  children,
  testId,
  disabled,
  contentWidth = false,
  isOpen: initialLoadOpen,
  theme,
  variant = AccordionItemVariant.MEDIUM,
  onToggle,
}: // className,
AccordionItemProps) => {
  const [isOpen, setIsOpen] = useState(!!initialLoadOpen);

  useEffect(() => {
    setIsOpen(initialLoadOpen);
  }, [initialLoadOpen, disabled, contentWidth, variant]);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    if (onToggle && typeof onToggle === 'function') {
      onToggle(!isOpen);
    }
  };

  const onMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.button === 0) {
      if (!disabled) {
        toggleOpen();
      }
    }
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const { key } = e;
    if ((key === 'Enter' || key === ' ') && !disabled) {
      toggleOpen();
    }
  };

  return (
    <>
      <div className="border-bottom border-gray-300">
        {React.Children.map(children, (child, idx) => {
          if (
            React.isValidElement(child) &&
            child.type === AccordionItem.Title
          ) {
            return React.cloneElement(child as React.ReactElement<any>, {
              idx,
              uniqueId: testId,
              isOpen,
              disabled,
              variant,
              onMouseDown,
              onKeyUp,
              theme,
            });
          } else if (
            React.isValidElement(child) &&
            child.type === AccordionItem.Body
          ) {
            return React.cloneElement(child as React.ReactElement<any>, {
              idx,
              uniqueId: testId,
              isOpen,
              contentWidth,
            });
          }

          return null;
        })}
      </div>
    </>
  );
};

const AccordionItemTitle = ({
  variant,
  onKeyUp,
  onMouseDown,
  uniqueId,
  isOpen,
  disabled,
  className,
  children,
  theme,
}: AccordionItemTitleProps) => {
  const pad = variant === 'small' ? 'sm' : 'md';

  return (
    <button
      onKeyUp={onKeyUp}
      onMouseDown={onMouseDown}
      aria-controls={uniqueId}
      aria-expanded={isOpen}
      className={`btn-reset width-full flex flex-items-top justify-between pad-r-xxs text-left pad-b-${pad} pad-t-${pad} currentColor`}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      type="button"
    >
      <span
        className={`${
          disabled ? 'color-ui-medium-gray' : ''
        } font-bold mar-b-none ${className || 'type-lg'}`}
        id={`${uniqueId}-btn`}
      >
        {children}
      </span>
      <ExpandIcon
        isExpand={isOpen}
        isDarkTheme={theme === 'theme-dark-accent-bg'}
      />
    </button>
  );
};
AccordionItem.Title = AccordionItemTitle;

const AccordionItemBody = ({
  isOpen,
  uniqueId,
  contentWidth,
  children,
}: AccordionItemBodyProps) => {
  const windowSize = useWindowSize();
  const divRef = useRef(null);
  const [height, setHeight] = useState(0);

  const onMutation = () => {
    setHeight(divRef.current.scrollHeight);
  };

  useMutationObserver(divRef.current, onMutation, {
    attributeOldValue: false,
    attributes: false,
    characterData: true,
    characterDataOldValue: false,
    childList: true,
    subtree: true,
  });

  useEffect(() => {
    setHeight(divRef.current?.scrollHeight);
  }, [windowSize, isOpen, divRef.current]);

  const style: CSSProperties = {
    maxHeight: isOpen ? `${height}px` : '0px',
  };

  return (
    <>
      <div
        aria-hidden={!isOpen}
        className={` line-h-lg overflow-hidden ${
          !isOpen
            ? `animate-slide-close hide-focusable`
            : `animate-slide-open hide-focusable`
        } text-left ${contentWidth ? `read-width` : ``}`}
        id={uniqueId}
        role="region"
        aria-labelledby={`${uniqueId}-btn`}
        ref={divRef}
        style={style}
      >
        <span className="block font-regular pad-b-md type-sm">{children}</span>
      </div>
      <style jsx>{`
        .animate-slide-close.hide-focusable {
          visibility: hidden;
          transition: max-height 0.25s,
            visibility 0.25s cubic-bezier(0.445, 0.05, 0.55, 0.95);
        }
        .animate-slide-open.hide-focusable {
          visibility: visible;
          transition: max-height 0.25s,
            visibility 0.25s cubic-bezier(0.445, 0.05, 0.55, 0.95);
        }
        .read-width {
          max-width: 1068px;
        }
      `}</style>
    </>
  );
};
AccordionItem.Body = AccordionItemBody;
